import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PublicSiteSettings } from "@/admin/types";
import { fetchSiteSettings } from "@/api/siteSettings";
import { applySiteTheme } from "@/theme/applySiteTheme";

type SiteSettingsContextValue = {
  settings: PublicSiteSettings | null;
  isLoading: boolean;
};

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  settings: null,
  isLoading: true,
});

export const SiteSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [settings, setSettings] = useState<PublicSiteSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchSiteSettings()
      .then((data) => {
        if (!mounted) return;
        setSettings(data);
        applySiteTheme(data);
      })
      .catch(() => {
        if (!mounted) return;
        setSettings(null);
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo(() => ({ settings, isLoading }), [settings, isLoading]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
